import {useEffect, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {map, Subscription, takeWhile} from 'rxjs';
import {
  ClientStatus,
  Conversation,
  ConversationLogItem,
  ConversationStatus,
} from '@stripe-internal/conversations-client';
import {useAnalytics} from '../AnalyticsProvider';
import {Input} from '../Chat/Input';
import {ConnectionNotice} from '../Chat/ConnectionNotice';
import {isAssistantEntryPoint} from '../StripeAssistant/analytics-utils';
import {useNewUniversalHelpDesign} from '../FeatureFlagsProvider';

export const messages = defineMessages({
  emptyPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.emptyPlaceholder',
    description:
      'Label for the chat textbox when no messages have been sent yet',
    defaultMessage: 'How can we help?',
  },
  replyPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.replyPlaceholder',
    description: 'Label for the chat textbox when there is an ongoing chat',
    defaultMessage: 'Write a reply…',
  },
  promptPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.promptPlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'Ask a question',
  },
});

const logContainsUserMessage = (items: ConversationLogItem[]): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'user',
  );
};

const logContainsAgentMessages = (items: ConversationLogItem[]): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'agent',
  );
};

export const ChatInput = ({
  conversation,
  attachmentsDisabled = false,
  hidden = false,
  animate = true,
}: {
  conversation: Conversation;
  attachmentsDisabled?: boolean;
  hidden?: boolean;
  animate?: boolean;
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const useNewDesign = useNewUniversalHelpDesign();

  const [conversationStatus, setConversationStatus] =
    useState<ConversationStatus>(conversation.status);
  const [clientStatus, setClientStatus] = useState<ClientStatus>(
    conversation.client.getStatus(),
  );

  const [agentHasSentMessage, setAgentHasSentMessage] = useState<boolean>(
    logContainsAgentMessages(conversation.log.get()),
  );
  const [userHasSentMessage, setUserHasSentMessage] = useState<boolean>(
    logContainsUserMessage(conversation.log.get()),
  );

  useEffect(() => {
    const sub = new Subscription();
    sub.add(conversation.statusObservable().subscribe(setConversationStatus));
    sub.add(conversation.client.statusObservable().subscribe(setClientStatus));
    return () => sub.unsubscribe();
  }, [conversation]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !agentHasSentMessage),
        map(logContainsAgentMessages),
      )
      .subscribe(setAgentHasSentMessage);
    return () => sub.unsubscribe();
  }, [conversation, agentHasSentMessage]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !userHasSentMessage),
        map(logContainsUserMessage),
      )
      .subscribe(setUserHasSentMessage);
    return () => sub.unsubscribe();
  }, [conversation, userHasSentMessage]);

  if (conversationStatus !== ConversationStatus.Active && !useNewDesign) {
    return null;
  }

  if (
    clientStatus === ClientStatus.Offline ||
    clientStatus === ClientStatus.Reconnecting
  ) {
    return <ConnectionNotice status="reconnecting" />;
  }
  const inputDisabled = clientStatus !== ClientStatus.Connected;

  let placeholder;
  if (conversation.context.channel === 'bot') {
    // always display prompt placeholder against bots
    placeholder = intl.formatMessage(messages.promptPlaceholder);
  } else {
    // if in an actual human-to-human interaction, display empty vs reply depending on if the agent has replied
    placeholder = agentHasSentMessage
      ? intl.formatMessage(messages.replyPlaceholder)
      : intl.formatMessage(messages.emptyPlaceholder);
  }

  const handleSendMessage = async (message: string) => {
    if (message.trim() === '') {
      return;
    }

    const msg = conversation.buildTextMessage(message);
    await conversation.sendMessage(msg);

    if (isAssistantEntryPoint(conversation)) {
      analytics.action('ai_assistant_message_sent', {
        ai_assistant_message_content: message,
      });
    }
  };

  const handleSendFile = async (file: File) => {
    const msg = conversation.buildAttachmentMessage(file);
    await conversation.sendMessage(msg);
  };

  const handleSendTyping = () => {
    conversation.sendTyping();
  };

  return (
    <Input
      disabled={inputDisabled}
      placeholder={placeholder}
      onSendMessage={handleSendMessage}
      onSendFile={handleSendFile}
      onSendTyping={handleSendTyping}
      attachmentDisabled={attachmentsDisabled || !userHasSentMessage}
      hidden={hidden}
      animate={animate}
    />
  );
};
